import { useMemo } from 'react';
import { TFunction } from 'i18next';

import {
  BoardMineralization,
  BoardSoil,
  BoardStatus,
} from 'features/Croptimizer/types';
import { numericValues } from 'utils/helpers';
import { getEfficiencyFormula } from 'features/Croptimizer/utils/calcHelperBoard';

interface Props {
  getY3CurrentProfit?: number | string;
  mineralizationValues: BoardMineralization | null;
  boardStatusValues: BoardStatus | null;
  boardSoilValues: BoardSoil | null;
  optimumValue: number;
  t: TFunction;
}

const Y4CurrentProfitFirst = ({
  getY3CurrentProfit,
  boardStatusValues,
  mineralizationValues,
  boardSoilValues,
  optimumValue,
  t,
}: Props) => {
  const result = useMemo(() => {
    if (
      getY3CurrentProfit &&
      boardStatusValues &&
      mineralizationValues &&
      boardSoilValues
    ) {
      const getSec = (
        ((Math.tan((Number(getY3CurrentProfit) - 5.8) / 1.45) * 54 + 70) /
          100 /
          0.05) *
        5
      ).toFixed(2);

      const ag38 = getEfficiencyFormula(
        Number(mineralizationValues?.month),
        Number(boardStatusValues?.currentPh),
        Number(numericValues(boardStatusValues?.currentSec)),
        // Number(boardSoilValues?.selected),
        optimumValue,
        Number(numericValues(mineralizationValues?.soilTillage)),
      );
      const ah38 = getEfficiencyFormula(
        24,
        Number(getY3CurrentProfit),
        Number(getSec),
        // Number(boardSoilValues?.selected),
        optimumValue,
        Number(numericValues(mineralizationValues?.soilTillage)),
      );
      const ai38 = getEfficiencyFormula(
        36,
        Number(getY3CurrentProfit),
        Number(getSec),
        // Number(boardSoilValues?.selected),
        optimumValue,
        Number(numericValues(mineralizationValues?.soilTillage)),
      );
      const aj38 = getEfficiencyFormula(
        48,
        Number(getY3CurrentProfit),
        Number(getSec),
        // Number(boardSoilValues?.selected),
        optimumValue,
        Number(numericValues(mineralizationValues?.soilTillage)),
      );

      const ag31 = ag38;
      const ah31 = ah38 < ag31 ? ag31 : ah38;
      const ai78 = ah31 / 100;
      const ah78 = ag31 / 100;
      const ah79 = ag31 / 100;
      const ai71 = ai78 - ah78;
      const ai64 = ai71 / Number(boardSoilValues?.split);
      const ah71 = ah78;
      const ah64 = ah71 / Number(boardSoilValues?.split);
      const dose = Number(numericValues(boardSoilValues?.dose)) / 100;
      const ai65 = (ah78 + ai71) / Number(boardSoilValues?.split);
      const ai79 = ah31 / 100;
      const ai31 = ai38 < ah31 ? ah31 : ai38;
      const aj78 = ai31 / 100;
      const aj71 = aj78 - ai78;
      const aj64 = aj71 / Number(boardSoilValues?.split);
      const aj79 = ai31 / 100;
      const aj72 = aj79 - ai79;
      const ai72 = ai79 - ah79;
      const aj65 = (aj72 + ai72) / Number(boardSoilValues?.split);
      const aj66 = (aj72 + ai72 + ah78) / Number(boardSoilValues?.split);
      const aj31 = aj38 < ai31 ? ai31 : aj38;
      const ajj78 = aj31 / 100;
      const ak71 = ajj78 - aj78;
      const ak64 = ak71 / Number(boardSoilValues?.split);
      const aj81 = ai31 / 100;
      const ak81 = aj31 / 100;
      const ak74 = ak81 - aj81;
      const ah81 = ag31 / 100;
      const ah74 = ah81;
      const ai81 = ah31 / 100;
      const aj74 = aj81 - ai81;
      const ai74 = ai81 - ah81;
      const ak67 = (ak74 + aj74 + ai74 + ah74) / Number(boardSoilValues?.split);
      const aj67 = (aj74 + ai74 + ah74) / Number(boardSoilValues?.split);
      const ai67 = (ai74 + ah74) / Number(boardSoilValues?.split);
      const ah67 = ah74 / 4;

      const ah72 = ah79;
      const ah65 = ah72 / 2;

      const ah80 = ag31 / 100;
      const ah73 = ah80;
      const ah66 = ah73 / 3;
      const ai80 = ah31 / 100;
      const ai73 = ai80 - ah80;
      const ai66 = (ai73 + ah73) / 3;
      const aj80 = ai31 / 100;
      const ak80 = aj31 / 100;
      const aj73 = aj80 - ai80;
      const ak73 = ak80 - aj80;
      const ak66 = (ak73 + aj73 + ai73) / 3;

      const ak79 = aj31 / 100;
      const ak72 = ak79 - aj79;
      const ak65 = (ak72 + aj72) / 2;
      const ah82 = ag31 / 100;
      const ah75 = ah82;
      const ai82 = ah31 / 100;
      const ai75 = ai82 - ah82;
      const aj82 = ai31 / 100;
      const aj75 = aj82 - ai82;
      const ak82 = aj31 / 100;
      const ak75 = ak82 - aj82;
      const ah68 = ah75 / 5;
      const ai68 = (ai75 + ah75) / 5;
      const aj68 = (aj75 + ai75 + ah75) / 5;
      const ak68 = (ak75 + aj75 + ai75 + ah75) / 5;

      if (mineralizationValues?.cap === t('no')) {
        if (boardSoilValues?.split === '1') {
          return (
            Number(getY3CurrentProfit) -
            Number(mineralizationValues?.annualDecrease)
          ).toFixed(2);
        }

        if (boardSoilValues?.split === '2') {
          return (
            Number(getY3CurrentProfit) -
            Number(mineralizationValues?.annualDecrease)
          ).toFixed(2);
        }

        if (boardSoilValues?.split === '3') {
          return (
            Number(getY3CurrentProfit) -
            Number(mineralizationValues?.annualDecrease)
          ).toFixed(2);
        }

        if (boardSoilValues?.split === '4') {
          const aa66 = Number(getY3CurrentProfit);
          const phcenter =
            Number(boardStatusValues?.targetPh) -
            Number(boardStatusValues?.currentPh);
          const centerDose = dose / Number(boardSoilValues?.split);

          return (
            aa66 +
            phcenter * centerDose -
            Number(mineralizationValues?.annualDecrease)
          ).toFixed(2);
        }

        if (boardSoilValues?.split === '5') {
          const aa66 = Number(getY3CurrentProfit);
          const phcenter =
            Number(boardStatusValues?.targetPh) -
            Number(boardStatusValues?.currentPh);
          const centerDose = dose / Number(boardSoilValues?.split);

          return (
            aa66 +
            phcenter * centerDose -
            Number(mineralizationValues?.annualDecrease)
          ).toFixed(2);
        }
      } else {
        if (boardSoilValues?.split === '1') {
          const first = Number(boardStatusValues?.currentPh);
          const second =
            Number(boardStatusValues?.targetPh) -
            Number(boardStatusValues?.currentPh);
          const third = ah64 + ai64 + aj64 + ak64;
          const fourth = Number(mineralizationValues?.annualDecrease) * 4;

          return (first + second * third - fourth).toFixed(2);
        }

        if (boardSoilValues?.split === '2') {
          const first = Number(boardStatusValues?.currentPh);
          const second =
            Number(boardStatusValues?.targetPh) -
            Number(boardStatusValues?.currentPh);

          const third = ah65 + ai65 + aj65 + ak65;
          const fourth = Number(mineralizationValues?.annualDecrease) * 4;

          return (first + second * third - fourth).toFixed(2);
        }

        if (boardSoilValues?.split === '3') {
          const first = Number(boardStatusValues?.currentPh);
          const second =
            Number(boardStatusValues?.targetPh) -
            Number(boardStatusValues?.currentPh);
          const third = ah66 + ai66 + aj66 + ak66;
          const fourth = Number(mineralizationValues?.annualDecrease) * 4;

          return (first + second * third - fourth).toFixed(2);
        }

        if (boardSoilValues?.split === '4') {
          const first = Number(boardStatusValues?.currentPh);
          const second =
            Number(boardStatusValues?.targetPh) -
            Number(boardStatusValues?.currentPh);
          const third = ah67 + ai67 + aj67 + ak67;
          const fourth = Number(mineralizationValues?.annualDecrease) * 4;

          return (first + second * third - fourth).toFixed(2);
        }

        if (boardSoilValues?.split === '5') {
          const first = Number(boardStatusValues?.currentPh);
          const second =
            Number(boardStatusValues?.targetPh) -
            Number(boardStatusValues?.currentPh);
          const third = ah68 + ai68 + aj68 + ak68;
          const fourth = Number(mineralizationValues?.annualDecrease) * 4;

          return (first + second * third - fourth).toFixed(2);
        }
      }
    }
  }, [
    getY3CurrentProfit,
    boardStatusValues,
    mineralizationValues,
    boardSoilValues,
  ]);

  return { result };
};

export default Y4CurrentProfitFirst;
